import { Component, effect, inject, input, OnInit, output, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AdminService } from '@miq/platform';
import { IOption, IUserPermission, ILocationPermission } from '@miq/shared-models';
import {
  FormGroup,
  FormControl,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { TitleCasePipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatDialogClose } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { L10nTranslationModule } from 'angular-l10n';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-global-selection',
  templateUrl: './global-selection.component.html',
  styleUrl: './global-selection.component.scss',
  imports: [
    MatButton,
    MatDialogClose,
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatOption,
    TitleCasePipe,
    L10nTranslationModule
],
})
export class GlobalSelectionComponent implements OnInit {
  public globalSection: FormGroup<{
    globalRegionFC: FormControl<IOption | null>;
    divisionFC: FormControl<IOption | null>;
    countryFC: FormControl<IOption | null>;
    marketFC: FormControl<IOption | null>;
    submarketFC: FormControl<IOption | null>;
    productTypeFC: FormControl<IOption | null>;
  }>;
  public searchPhrase: string;
  public globalRegionList: IOption[];
  public productTypeList: IOption[];
  public divisionList: IOption[];
  public countryList: IOption[];
  public marketList: IOption[];
  public subMarketList: IOption[];
  readonly selectedPermissionsInput = input<ILocationPermission>(undefined, { alias: 'selectedPermissions' });
  selectedPermissions: ILocationPermission;
  searchEmitter = output<any>()
  advancedSearchEmitter = output<any>()
  addPermissionEmitter = output<any>()
  // selection variables
  public selectedGlobalRegion: IOption;
  public selectedDivision: IOption;
  public selectedCountry: IOption;
  public selectedProductType: IOption;
  public selectedMarket: IOption;
  public selectedSubMarket: IOption;
  public userPermission: IUserPermission;
  public showEditDialogTitl = false;
  private readonly adminService = inject(AdminService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  constructor() {
    effect(() => {
      const v = this.selectedPermissionsInput();
      if (v !== undefined) this.selectedPermissions = v;
    });
  }
  ngOnInit() {
    const selectedPermissionsV = this.selectedPermissionsInput();
    if (selectedPermissionsV !== undefined) this.selectedPermissions = selectedPermissionsV;
    this.globalSection = new FormGroup({
      globalRegionFC: new FormControl<IOption | null>(null),
      divisionFC: new FormControl<IOption | null>(null),
      countryFC: new FormControl<IOption | null>(null),
      marketFC: new FormControl<IOption | null>(null),
      submarketFC: new FormControl<IOption | null>(null),
      productTypeFC: new FormControl<IOption | null>(null),
    });
    // initalizing userPermissions and assigning values on selection
    this.selectedGlobalRegion = { key: -1, value: '' };
    this.selectedMarket = { key: -1, value: '' };
    this.selectedCountry = { key: -1, value: '' };
    this.selectedSubMarket = { key: -1, value: '' };
    this.selectedProductType = { key: -1, value: '' };
    this.selectedDivision = { key: -1, value: '' };
    this.userPermission = {
      globalRegion: this.selectedGlobalRegion,
      division: this.selectedDivision,
      country: this.selectedCountry,
      market: this.selectedMarket,
      submarket: this.selectedSubMarket,
      productType: this.selectedProductType,
      status: { key: -1, value: '' },
    };
    if (this.selectedPermissions) {
      this.getUserPermissionPreference(this.selectedPermissions);
      this.showEditDialogTitl = true;
    }
    this.initializeDropDownValues();
    this.initializeFormControls();
  }
  public getUserPermissionPreference(selectedPermissions: ILocationPermission) {
    this.selectedGlobalRegion.key = this.selectedPermissions.globalRegionId;
    this.selectedGlobalRegion.value = this.selectedPermissions.globalRegionVal;
    this.selectedMarket.key = this.selectedPermissions.marketId;
    this.selectedMarket.value = this.selectedPermissions.marketVal;
    this.selectedSubMarket.key = this.selectedPermissions.subMarketId;
    this.selectedSubMarket.value = this.selectedPermissions.subMarketVal;
    this.selectedDivision.key = this.selectedPermissions.divisionId;
    this.selectedDivision.value = this.selectedPermissions.divisionVal;
    this.selectedCountry.key = this.selectedPermissions.countryId;
    this.selectedCountry.value = this.selectedPermissions.countryVal;
    this.selectedProductType.key = this.selectedPermissions.propertyUsageTypeId;
    this.selectedProductType.value = this.selectedPermissions.propertyUsageTypeVal;
  }
  /*
   *Initialze form controls
   */
  public initializeFormControls() {
    this.globalSection.controls.globalRegionFC.valueChanges.subscribe(value => {
      if (value) {
        this.selectedGlobalRegion = value;
        this.getDivision(this.selectedGlobalRegion.key);
        this.userPermission.globalRegion = value;
        this.addPermissionEmitter.emit(this.userPermission);
        this.selectedGlobalRegion.key === 0
          ? this.globalSection.controls.divisionFC.disable()
          : this.globalSection.controls.divisionFC.enable();
        this.cdr.markForCheck();
      }
    });
    this.globalSection.controls.divisionFC.valueChanges.subscribe(value => {
      if (this.globalSection.controls.divisionFC.dirty) {
        this.selectedPermissions = null;
      }
      if (value) {
        this.selectedDivision = value;
        this.getCountries(this.selectedDivision.key);
        this.userPermission.division = value;
        this.addPermissionEmitter.emit(this.userPermission);
        this.selectedDivision.key === 0
          ? this.globalSection.controls.countryFC.disable()
          : this.globalSection.controls.countryFC.enable();
        this.cdr.markForCheck();
      }
    });
    this.globalSection.controls.countryFC.valueChanges.subscribe(value => {
      if (this.globalSection.controls.countryFC.dirty) {
        this.selectedPermissions = null;
      }
      if (value) {
        this.selectedCountry = value;
        this.getMarkets(this.selectedCountry.key);
        this.userPermission.country = value;
        this.addPermissionEmitter.emit(this.userPermission);
        this.selectedCountry.key === 0
          ? this.globalSection.controls.marketFC.disable()
          : this.globalSection.controls.marketFC.enable();
        this.cdr.markForCheck();
      }
    });
    this.globalSection.controls.marketFC.valueChanges.subscribe(value => {
      if (this.globalSection.controls.marketFC.dirty) {
        this.selectedPermissions = null;
      }
      if (value) {
        this.selectedMarket = value;
        this.getSubMarkets(this.selectedMarket.key);
        this.userPermission.market = value;
        this.addPermissionEmitter.emit(this.userPermission);
        this.selectedMarket.key === 0
          ? this.globalSection.controls.submarketFC.disable()
          : this.globalSection.controls.submarketFC.enable();
        this.cdr.markForCheck();
      }
    });
    this.globalSection.controls.submarketFC.valueChanges.subscribe(value => {
      if (this.globalSection.controls.submarketFC.dirty) {
        this.selectedPermissions = null;
      }
      if (value) {
        this.selectedSubMarket = value;
        this.userPermission.submarket = value;
        this.addPermissionEmitter.emit(this.userPermission);
        if (this.selectedSubMarket && this.selectedSubMarket.key === 0) {
          this.advancedSearchEmitter.emit('close');
        }
        this.cdr.markForCheck();
      }
    });
    this.globalSection.controls.productTypeFC.valueChanges.subscribe(value => {
      if (this.globalSection.controls.marketFC.dirty) {
        this.selectedPermissions = null;
      }
      if (value) {
        this.selectedProductType = value;
        this.userPermission.productType = value;
        this.addPermissionEmitter.emit(this.userPermission);
        this.cdr.markForCheck();
      }
    });
  }
  /**
   * gets the values for drop downs
   */
  public initializeDropDownValues() {
    this.adminService.getPropertyUsageTypes().subscribe(result => {
      this.productTypeList = result;
      if (this.selectedPermissions) {
        this.globalSection.controls.productTypeFC.setValue(
          result.find(f => f.key === this.selectedPermissions.propertyUsageTypeId)
        );
        this.userPermission.productType = result.find(f => f.key === this.selectedPermissions.propertyUsageTypeId);
      } else {
        this.selectedProductType = result[0];
        this.userPermission.productType = result[0];
        this.globalSection.controls.productTypeFC.setValue(result[0]);
        // this.addPermissionEmitter.emit(this.userPermission);
      }
      this.cdr.markForCheck();
    });
    this.getGlobalRegions();
    this.addPermissionEmitter.emit(this.userPermission);
  }
  // advanced search event emitter
  public advancedSearch() {
    this.advancedSearchEmitter.emit(this.selectedSubMarket);
  }
  public get globalRegionFC() {
    return this.globalSection.get('globalRegionFC');
  }
  public get regionFC() {
    return this.globalSection.get('regionFC');
  }
  public get marketFC() {
    return this.globalSection.get('marketFC');
  }
  public get submarketFC() {
    return this.globalSection.get('submarketFC');
  }
  public get productTypeFC() {
    return this.globalSection.get('productTypeFC');
  }
  /*
   *API Calls to get region/market/global region
   */
  public getGlobalRegions(): Subscription {
    return this.adminService.getGlobalRegions().subscribe(this.onGlobalRegionsReceived.bind(this));
  }
  public onGlobalRegionsReceived(result): void {
    this.globalRegionList = result;
    if (this.selectedPermissions) {
      this.globalSection.controls.globalRegionFC.setValue(
        result.find(f => f.key === this.selectedPermissions.globalRegionId)
      );
      this.userPermission.globalRegion = result.find(f => f.key === this.selectedPermissions.globalRegionId);
    } else {
      this.selectedGlobalRegion = result[0];
      this.userPermission.globalRegion = result[0];
      this.globalSection.controls.globalRegionFC.setValue(result[0]);
    }
    this.cdr.markForCheck();
  }
  public getDivision(selectedGlobalRegion: number): void {
    this.adminService.getDivisions(selectedGlobalRegion).subscribe(result => {
      this.divisionList = result;
      if (this.selectedPermissions) {
        const firstDivision = this._getGeoOption(this.divisionList, this.selectedPermissions.divisionId);
        this.globalSection.controls.divisionFC.setValue(firstDivision);
        this.userPermission.division = firstDivision;
      } else {
        this.selectedDivision = result[0];
        this.userPermission.division = result[0];
        this.globalSection.controls.divisionFC.setValue(result[0]);
      }
      this.cdr.markForCheck();
    });
  }
  public getCountries(selectedDivision: number): void {
    this.adminService.getCountries(selectedDivision).subscribe(result => {
      this.countryList = result;
      if (this.selectedPermissions) {
        const firstCountry = this._getGeoOption(this.countryList, this.selectedPermissions.countryId);
        this.globalSection.controls.countryFC.setValue(firstCountry);
        this.userPermission.country = firstCountry;
      } else {
        this.selectedCountry = result[0];
        this.userPermission.country = result[0];
        this.globalSection.controls.countryFC.setValue(result[0]);
      }
      this.cdr.markForCheck();
    });
  }
  public getMarkets(selectedCountry: number): void {
    this.adminService.getMarkets(selectedCountry).subscribe(result => {
      this.marketList = result;
      if (this.selectedPermissions) {
        const firstMarket = this._getGeoOption(this.marketList, this.selectedPermissions.marketId);
        this.globalSection.controls.marketFC.setValue(firstMarket);
        this.userPermission.market = firstMarket;
      } else {
        this.selectedMarket = result[0];
        this.userPermission.market = result[0];
        this.globalSection.controls.marketFC.setValue(result[0]);
      }
      this.cdr.markForCheck();
    });
  }
  public getSubMarkets(selectedMarket: number): void {
    this.adminService.getSubMarkets(selectedMarket).subscribe(result => {
      this.subMarketList = result;
      if (this.selectedPermissions) {
        const firstSubMarket = this._getGeoOption(this.subMarketList, this.selectedPermissions.subMarketId);
        this.globalSection.controls.submarketFC.setValue(firstSubMarket);
        this.userPermission.submarket = firstSubMarket;
      } else {
        this.selectedSubMarket = result[0];
        this.userPermission.submarket = result[0];
        this.globalSection.controls.submarketFC.setValue(result[0]);
      }
      this.cdr.markForCheck();
    });
  }
  private _getGeoOption(dataSource: IOption[], targetValue: any): IOption {
    const [firstItem] = dataSource;
    return dataSource.find(option => option.key === targetValue) || firstItem;
  }
}
